/**
 * Best Sellers Section - Tab Switching & AJAX Add to Cart
 */

(function () {
  class BestSellers {
    constructor() {
      this.sections = document.querySelectorAll('.best-sellers[data-section-id]');
      this.init();
    }

    init() {
      this.sections.forEach((section) => {
        this.initSection(section);
      });
    }

    initSection(section) {
      const sectionId = section.dataset.sectionId;
      const tabs = section.querySelectorAll('.best-sellers__tab');
      const productGroups = section.querySelectorAll('.best-sellers__product-group');

      // Tab click handlers
      tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          const targetTab = tab.dataset.tab;
          this.switchTab(section, targetTab, tabs, productGroups);
        });
      });

      // AJAX add to cart handlers
      section.addEventListener('click', (e) => {
        const addButton = e.target.closest('[data-best-sellers-add]');
        if (addButton) {
          e.preventDefault();
          this.handleAddToCart(addButton);
        }
      });

      // Initialize scroll indicators for product grids on mobile
      this.initScrollIndicators(section);
      
      // Initialize scroll buttons
      this.initScrollButtons(section);
    }

    initScrollIndicators(section) {
      const grids = section.querySelectorAll('.best-sellers__grid');
      
      grids.forEach((grid) => {
        const updateScrollIndicator = () => {
          const isScrollable = grid.scrollWidth > grid.clientWidth;
          const isScrolledToEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 10; // 10px threshold
          
          if (isScrollable && !isScrolledToEnd) {
            grid.classList.remove('scrolled-to-end');
          } else {
            grid.classList.add('scrolled-to-end');
          }
        };

        // Check on load and resize
        updateScrollIndicator();
        window.addEventListener('resize', updateScrollIndicator);
        
        // Check on scroll
        grid.addEventListener('scroll', updateScrollIndicator);
        
        // Also check when tab switches (new content might be loaded)
        const observer = new MutationObserver(updateScrollIndicator);
        observer.observe(grid, { childList: true, subtree: true });
      });
    }

    initScrollButtons(section) {
      const productGroups = section.querySelectorAll('.best-sellers__product-group');
      
      productGroups.forEach((productGroup) => {
        const grid = productGroup.querySelector('.best-sellers__grid');
        const prevButton = productGroup.querySelector('.best-sellers__scroll-button--prev');
        const nextButton = productGroup.querySelector('.best-sellers__scroll-button--next');
        
        if (!grid || !prevButton || !nextButton) return;
        
        // Update button visibility based on scroll position
        const updateButtonVisibility = () => {
          const scrollLeft = grid.scrollLeft;
          const maxScroll = grid.scrollWidth - grid.clientWidth;
          
          // Hide prev button at start
          if (scrollLeft <= 10) {
            prevButton.classList.add('hidden');
          } else {
            prevButton.classList.remove('hidden');
          }
          
          // Hide next button at end
          if (scrollLeft >= maxScroll - 10) {
            nextButton.classList.add('hidden');
          } else {
            nextButton.classList.remove('hidden');
          }
        };
        
        // Initial visibility check
        updateButtonVisibility();
        
        // Update on scroll
        grid.addEventListener('scroll', updateButtonVisibility);
        
        // Update on resize
        window.addEventListener('resize', updateButtonVisibility);
        
        // Handle button clicks
        const handleScroll = (direction) => {
          // Calculate scroll amount: approximately 2 product cards
          const cardWidth = 280 + 16; // card width + gap
          const scrollAmount = cardWidth * 2;
          
          const currentScroll = grid.scrollLeft;
          let targetScroll;
          
          if (direction === 'prev') {
            targetScroll = Math.max(0, currentScroll - scrollAmount);
          } else {
            const maxScroll = grid.scrollWidth - grid.clientWidth;
            targetScroll = Math.min(maxScroll, currentScroll + scrollAmount);
          }
          
          grid.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
          });
          
          // Update button visibility after scroll
          setTimeout(updateButtonVisibility, 500);
        };
        
        prevButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleScroll('prev');
        });
        
        nextButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleScroll('next');
        });
      });
    }

    switchTab(section, targetTab, tabs, productGroups) {
      // Update active tab
      tabs.forEach((tab) => {
        if (tab.dataset.tab === targetTab) {
          tab.classList.add('active');
        } else {
          tab.classList.remove('active');
        }
      });

      // Show corresponding product group
      productGroups.forEach((group) => {
        if (group.dataset.tabGroup === targetTab) {
          group.classList.add('active');
          // Re-trigger animation
          group.style.animation = 'none';
          setTimeout(() => {
            group.style.animation = 'bestSellersFadeIn 0.4s ease-in-out';
          }, 10);
          
          // Reset scroll position and update button visibility
          const grid = group.querySelector('.best-sellers__grid');
          const prevButton = group.querySelector('.best-sellers__scroll-button--prev');
          const nextButton = group.querySelector('.best-sellers__scroll-button--next');
          
          if (grid) {
            grid.scrollLeft = 0;
            
            // Update button visibility after a short delay
            setTimeout(() => {
              if (prevButton) prevButton.classList.add('hidden');
              if (nextButton) {
                const maxScroll = grid.scrollWidth - grid.clientWidth;
                if (maxScroll <= 10) {
                  nextButton.classList.add('hidden');
                } else {
                  nextButton.classList.remove('hidden');
                }
              }
            }, 50);
          }
        } else {
          group.classList.remove('active');
        }
      });
    }

    async handleAddToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;

      button.disabled = true;
      const originalText = button.querySelector('.best-sellers__quick-add-text').textContent;
      button.querySelector('.best-sellers__quick-add-text').textContent = '...';

      try {
        const response = await this.addToCart(variantId);
        button.querySelector('.best-sellers__quick-add-text').textContent = 'ADDED!';

        // Update cart
        const cartData = await this.fetchCart();
        this.updateCartUI(cartData || response);

        // Publish event if available
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'best-sellers',
            productVariantId: variantId,
            cartData: cartData || response,
          });
        }
      } catch (error) {
        button.querySelector('.best-sellers__quick-add-text').textContent = 'ERROR';
      } finally {
        setTimeout(() => {
          button.querySelector('.best-sellers__quick-add-text').textContent = originalText;
          button.disabled = false;
        }, 1500);
      }
    }

    async addToCart(variantId) {
      const baseUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';
      const url = baseUrl.endsWith('.js') ? baseUrl : `${baseUrl}.js`;

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || (data && data.status)) {
        throw new Error((data && (data.description || data.message)) || 'Failed to add to cart');
      }

      return data;
    }

    async fetchCart() {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        return await response.json();
      } catch (_e) {
        return null;
      }
    }

    updateCartUI(cartData) {
      // Update cart drawer/notification if present
      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');
        if (cartDrawer && cartData) cartDrawer.renderContents(cartData);
        if (cartNotification && cartData) cartNotification.renderContents(cartData);
      } catch (_e) {}

      // Update cart count - multiple selectors for different header implementations
      if (cartData && cartData.item_count !== undefined) {
        const count = cartData.item_count;
        
        // Standard cart count bubble (header.liquid) - update the span inside
        document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
          const span = bubble.querySelector('span[aria-hidden="true"]');
          if (span) {
            span.textContent = count;
          } else {
            bubble.textContent = count;
          }
          // Show/hide based on count
          if (count > 0) {
            bubble.style.display = '';
            bubble.closest('.header__icon--cart')?.classList.remove('cart-empty');
          } else {
            bubble.style.display = 'none';
            bubble.closest('.header__icon--cart')?.classList.add('cart-empty');
          }
        });
        
        // Data attribute selectors
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = count;
          el.style.display = count > 0 ? '' : 'none';
        });
        
        // Luxury header specific IDs (header-luxury.liquid)
        const cartCountEl = document.getElementById('cart-count');
        const mobileCartCountEl = document.getElementById('mobile-cart-count');
        const topbarCartCountEl = document.getElementById('topbar-cart-count');
        
        if (cartCountEl) {
          cartCountEl.textContent = count;
          cartCountEl.style.display = count > 0 ? 'flex' : 'none';
        }
        
        if (mobileCartCountEl) {
          mobileCartCountEl.textContent = count;
          mobileCartCountEl.style.display = count > 0 ? 'flex' : 'none';
        }
        
        if (topbarCartCountEl) {
          topbarCartCountEl.textContent = count;
          topbarCartCountEl.style.display = count > 0 ? 'flex' : 'none';
        }
        
        // Trigger custom event for header-luxury updateCartCount listener
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
      }
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new BestSellers();
    });
  } else {
    new BestSellers();
  }
})();

